import './style.scss';
import { CreateWebsiteSteps } from '../Components/CreateSystemConfigurationSteps/CreateSystemConfigurationSteps';
import {
  useToastContext,
  useForm,
  TruForm,
  PendingMarkIcon,
  CheckMarkIcon,
  ErrorMarkIcon,
  Loading,
  TruFormButton,
} from '@trufla-technology/trumarket-react-design-system';
import {
  CreateWebsiteBody,
  GetWebsiteInfo,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { useGetWebsiteInfo } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { IErrorResponse } from 'app/types/ErrorResponse';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
export type props = {
  envData?: GetWebsiteInfo;
};

export const DeploymentDetails = ({ envData }: props) => {
  const history = useHistory<{
    websiteName: string;
  }>();

  const { state } = history.location;
  const form = useForm<CreateWebsiteBody>();

  const { data, get } = useGetWebsiteInfo();
  const [loading, setLoading] = useState<boolean>(false);

  const getStatusAndIcon = (
    environmentType: 'production' | 'development' | 'staging',
  ) => {
    const status = data?.environments?.[environmentType]?.status || null;
    let className = 'card';
    let icon = null;

    if (status === 'active') {
      className = 'card';
      icon = <CheckMarkIcon width="18px" />;
    } else if (status === 'pending') {
      className = 'card border-color-orange';
      icon = <PendingMarkIcon width="18px" fill="var(--tru-yellow-color)" />;
    } else if (status === 'not_exists') {
      className = 'card border-color-red';
      icon = <ErrorMarkIcon width="18px" />;
    } else if (status === 'ftp_generated') {
      className = 'card';
      icon = <CheckMarkIcon width="18px" />;
    } else if (status === 'ftp_verified') {
      className = 'card';
      icon = <CheckMarkIcon width="18px" />;
    } else {
      className = 'card';
      icon = '';
    }

    return { className, icon };
  };
  const production = getStatusAndIcon('production');
  const development = getStatusAndIcon('development');
  const staging = getStatusAndIcon('staging');

  const toaster = useToastContext();

  const handleFinishStep = async () => {
    history.push(`/configurations/system-configuration/finalization`);
  };
  const handleVerify = async () => {
    try {
      if (!state) {
        setLoading(true);
        await get();
        setLoading(false);
      }
    } catch (err: unknown) {
      const errorMessage = (
        err as IErrorResponse
      )?.response?.data?.error.replaceAll('body.', '');
      toaster.error(errorMessage || 'Failed to add a site');
    }
  };

  function handleStatus() {
    if (data?.environments) {
      return Object.values(data?.environments!).some((nestedObj) => {
        const { status } = nestedObj;
        return status === 'not_exists' || status === 'pending';
      });
    }
  }
  const statusResult = handleStatus();
  useEffect(() => form.reset(), [form]);

  return (
    <div className="site-details">
      <div className="form">
        <TruForm onFinish={handleFinishStep} form={form}>
          {<CreateWebsiteSteps step={2} disableFinish={statusResult} />}

          {!data ? (
            <div className="loading-wrapper">
              <Loading />
            </div>
          ) : (
            <div className="details">
              <div className="deployment-details">
                <h4>Deployment Details</h4>
                <p>
                  Click on the buttons to configure each URL, and follow the
                  below pattern. For details please refer to
                  <a
                    href="https://www.trufla.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Documentations.
                  </a>
                </p>
              </div>
              <div className="environments">
                <div className="environment-card">
                  <div className={production.className}>
                    <div className="environment-info">
                      <h2 className="environment-name">Production</h2>
                      <p className="environment-branch">
                        Should be:
                        <span>{data?.environments?.production?.name}</span>
                      </p>
                      {data?.environments?.production?.status === 'pending' ? (
                        <p className="environment-pending-msg">Pending</p>
                      ) : data?.environments?.production?.status ===
                        'not_exists' ? (
                        <p className="environment-error-msg">Not Active</p>
                      ) : null}
                    </div>
                    <div className="environment-configure-actions">
                      <a
                        href={`https://my.wpengine.com/installs/${data?.environments?.production?.name}`}
                        className="primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Configure
                      </a>
                    </div>
                  </div>
                  <div className="status">{production.icon}</div>
                </div>
                <div className="environment-card">
                  <div className={development.className}>
                    <div className="environment-info">
                      <h2 className="environment-name">Staging</h2>
                      <p className="environment-branch">
                        Should be:
                        <span>{data?.environments?.staging?.name}</span>
                      </p>
                      {data?.environments?.staging?.status === 'pending' ? (
                        <p className="environment-pending-msg">Pending</p>
                      ) : data?.environments?.staging?.status ===
                        'not_exists' ? (
                        <p className="environment-error-msg">Not Active</p>
                      ) : null}
                    </div>
                    <div className="environment-configure-actions">
                      <a
                        href={`https://my.wpengine.com/installs/${data?.environments?.staging?.name}`}
                        className="primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Configure
                      </a>
                    </div>
                  </div>
                  <div className="status">{staging.icon}</div>
                </div>
                <div className="environment-card">
                  <div className={staging.className}>
                    <div className="environment-info">
                      <h2 className="environment-name">Development</h2>
                      <p className="environment-branch">
                        Should be:
                        <span>{data?.environments?.development?.name}</span>
                      </p>
                      {data?.environments?.development?.status === 'pending' ? (
                        <p className="environment-pending-msg">Pending</p>
                      ) : data?.environments?.development?.status ===
                        'not_exists' ? (
                        <p className="environment-error-msg">Not Active</p>
                      ) : null}
                    </div>
                    <div className="environment-configure-actions">
                      <a
                        href={`https://my.wpengine.com/installs/${data?.environments?.development?.name}`}
                        className="primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Configure
                      </a>
                    </div>
                  </div>
                  <div className="status">{development.icon}</div>
                </div>

                <TruFormButton
                  onClick={handleVerify}
                  type="primary"
                  htmlType="button"
                  disabled={!statusResult}
                  loading={loading}
                >
                  Verify
                </TruFormButton>
              </div>
              <div className="environment-instructions">
                <ol>
                  <li>
                    Go to
                    <a
                      href="https://www.trufla.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WP-Engines
                    </a>
                  </li>
                  <li>Select “Copy an existing environment to this site” </li>
                  <li>
                    From the drop-down menu, select “webfoundation” project
                  </li>
                  <li>Click Next</li>
                </ol>
                <p>
                  For details, please refer to
                  <a
                    href="https://www.trufla.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Documentations.
                  </a>
                </p>
              </div>
            </div>
          )}
        </TruForm>
      </div>
    </div>
  );
};
